/**
 * The single outbound HTTP seam.
 *
 * Error parsing is delegated to `@cedarreachai/api-client` so the §13.1 envelope
 * has exactly one implementation in the repository: every failure surfaces as
 * `ApiClientError` carrying the `ApiFailure` discriminated union, and UI code
 * switches on `failure.code` rather than matching messages.
 *
 * The wrapper here exists instead of `createApiClient` because the live router
 * is workspace-scoped and is not yet described by `contracts/openapi.yaml`
 * (see `./types.ts`). The cross-cutting behaviour is the same: correlation id
 * out, `If-Match` when a version is given, and `Idempotency-Key` exactly when
 * the caller supplies one — one key per user action, from `useActionKey` —
 * and never minted per call (see {@link idempotencyKeyFor}).
 */
import {
  ApiClientError,
  parseErrorEnvelope,
  requiresIdempotencyKey,
} from '@cedarreachai/api-client';
import { env } from '../env';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | undefined>>;
  /**
   * The user action's key (`useActionKey`), reused across its retries — that
   * is the point. Omit it and no key is sent; the contract's keyed
   * operations must always get one (a missing one throws in development).
   */
  readonly idempotencyKey?: string;
  readonly ifMatchVersion?: number | string;
  readonly signal?: AbortSignal;
}

/** The cookie the API sets at sign-in; its value is echoed on every mutation. */
export const CSRF_COOKIE = 'gp_csrf';
/** The header the API checks for cookie-authenticated mutations (ADR-014). */
export const CSRF_HEADER = 'X-CSRF-Token';

/**
 * The session's CSRF token, read from the `gp_csrf` cookie. A page on another
 * origin cannot read it, which is the whole defence. `null` when there is no
 * browser session (MSW, fake local identity, bearer clients).
 */
export function readCsrfToken(cookie: string = globalThis.document?.cookie ?? ''): string | null {
  for (const part of cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === CSRF_COOKIE && rest.length > 0) {
      const value = rest.join('=');
      return value.length > 0 ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

/**
 * The session's CSRF token as the API last handed it over in the
 * `X-CSRF-Token` response header. Only a web app served from an origin
 * listed in the API's `APP_WEB_ORIGIN` receives it (ADR-013 amendment): such
 * a page cannot read the API's `gp_csrf` cookie. Kept in memory only, never
 * in storage; the cookie wins whenever it is readable.
 */
let issuedCsrfToken: string | null = null;

/** The CSRF token to echo on a mutation: the cookie's, else the issued one. */
export function currentCsrfToken(): string | null {
  return readCsrfToken() ?? issuedCsrfToken;
}

/** Forgets the issued CSRF token (sign-out, tests). */
export function forgetIssuedCsrfToken(): void {
  issuedCsrfToken = null;
}

/** Why a request needs the person to sign in (again). */
export type AuthFailure = 'unauthenticated' | 'reauthenticate';

/**
 * The request the API refused, so the handler can say which action it was
 * (the step-up dialog restates it, design doc §22.3). `path` is the request
 * path without its query; `body` is what this client sent, never anything the
 * server returned.
 */
export interface AuthFailureContext {
  readonly method: string;
  readonly path: string;
  readonly body?: unknown;
  /**
   * The `Idempotency-Key` the refused request carried, if any: a step-up
   * action restored after a reload is sent again with this same key, so one
   * that did commit is answered from the server's replay store instead of
   * running twice (ADR-068).
   */
  readonly idempotencyKey?: string;
  /** The `If-Match` the refused request carried, if any (sent again unchanged). */
  readonly ifMatchVersion?: string;
  /**
   * This refusal's own id, also carried by the error the request rejects
   * with ({@link refusalIdOf}). The step-up dialog uses it to find the one
   * mutation this request belonged to (ADR-049), never "the latest pending".
   */
  readonly refusalId: string;
}

/** Each refused request's id, keyed by the error it rejected with. */
const refusalIds = new WeakMap<object, string>();

/**
 * The refusal id (see {@link AuthFailureContext.refusalId}) of a request that
 * rejected with `error`, or `null` for any other error.
 */
export function refusalIdOf(error: unknown): string | null {
  return typeof error === 'object' && error !== null ? (refusalIds.get(error) ?? null) : null;
}

/** What `AuthGate` installs: told why, and which request. */
export type AuthFailureHandler = (failure: AuthFailure, context: AuthFailureContext) => void;

let authFailureHandler: AuthFailureHandler | null = null;

/**
 * Registers what happens when the API says the caller is not signed in
 * (`401 UNAUTHENTICATED`) or must sign in again for this action
 * (`401 REAUTHENTICATION_REQUIRED`). The app's `AuthGate` installs it; the
 * request still rejects, so a screen's own error state is unchanged.
 */
export function setAuthFailureHandler(handler: AuthFailureHandler | null): void {
  authFailureHandler = handler;
}

/**
 * Whether the person is signing out right now. Once `POST /auth/logout` has
 * revoked the session, any request still in flight (a query refetch, a
 * poll) answers `401 UNAUTHENTICATED`. Those are the expected consequence of
 * signing out, not a lost session: sending the browser to `/auth/login` for
 * them would race the sign-out navigation and, with a provider that still
 * remembers the person, sign them straight back in.
 */
let signingOut = false;

/** Marks the start of a sign-out: `401 UNAUTHENTICATED` no longer navigates to sign-in. */
export function beginSignOut(): void {
  signingOut = true;
}

/**
 * Ends the sign-out window without leaving the app (the sign-out request
 * failed, so the session may well still be live and a 401 means what it
 * usually does). A successful sign-out needs no call: the browser leaves.
 */
export function cancelSignOut(): void {
  signingOut = false;
}

/** Whether a sign-out is in progress (tests). */
export function isSigningOut(): boolean {
  return signingOut;
}

function newRequestId(): string {
  return `req_${crypto.randomUUID()}`;
}

/** Thrown in development for a keyed operation called without a key. */
export class MissingIdempotencyKeyError extends Error {
  constructor(method: string, path: string) {
    super(
      `${method} ${path} requires an Idempotency-Key (contracts/openapi.yaml): pass the ` +
        "user action's key from useActionKey().keyFor(input) as `idempotencyKey`.",
    );
    this.name = 'MissingIdempotencyKeyError';
  }
}

/**
 * The `Idempotency-Key` to send, or `null` for none.
 *
 * Invariant: the caller's key is forwarded unchanged; without one, no key is
 * sent — a key minted per call replays nothing and only hides that the call
 * site forgot to give its retries one key. On an operation whose contract
 * requires a key, a missing one is a programming error: it throws in
 * development (and tests); a production build logs it and sends a one-off key
 * so the server does not refuse the person's action with a 422.
 */
export function idempotencyKeyFor(
  method: string,
  path: string,
  supplied: string | undefined,
  dev: boolean = import.meta.env.DEV,
): string | null {
  if (supplied !== undefined) return supplied;
  if (!MUTATING.has(method) || !requiresIdempotencyKey(method, path)) return null;
  if (dev) throw new MissingIdempotencyKeyError(method, path);
  console.error(new MissingIdempotencyKeyError(method, path).message);
  return crypto.randomUUID();
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${env.apiBaseUrl}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, value);
  }
  return url.toString();
}

/**
 * Performs one request and returns the decoded JSON body.
 *
 * @throws {ApiClientError} for every non-2xx response and for transport
 * failures, so a caller never has to decide whether an error is "real".
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const requestId = newRequestId();

  const headers = new Headers({
    Accept: 'application/json',
    'X-Request-Id': requestId,
  });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  const idempotencyKey = idempotencyKeyFor(method, path, options.idempotencyKey);
  if (idempotencyKey !== null) headers.set('Idempotency-Key', idempotencyKey);
  if (MUTATING.has(method)) {
    // Cookie-authenticated mutations must carry the session's CSRF token.
    const csrf = currentCsrfToken();
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  if (options.ifMatchVersion !== undefined) {
    headers.set('If-Match', String(options.ifMatchVersion));
  }
  // Local-development identity only. A production API refuses to start in the
  // mode that honours this header, so sending it can never be a way in.
  if (env.devUserId) headers.set('x-gp-dev-user', env.devUserId);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    throw new ApiClientError({
      kind: 'transport',
      message: cause instanceof Error ? cause.message : 'The request could not be sent.',
      requestId,
      cause,
      retriable: true,
    });
  }

  const responseRequestId = response.headers.get('X-Request-Id') ?? requestId;
  const issued = response.headers.get(CSRF_HEADER);
  if (issued) issuedCsrfToken = issued;

  if (!response.ok) {
    const text = await response.text();
    let body: unknown = text;
    try {
      body = text.length > 0 ? JSON.parse(text) : undefined;
    } catch {
      /* leave `body` as the raw text; parseErrorEnvelope reports it as malformed */
    }
    const failure = parseErrorEnvelope(response.status, body, responseRequestId);
    if (response.status === 401 && failure.kind === 'api' && failure.code === 'UNAUTHENTICATED') {
      issuedCsrfToken = null;
    }
    const error = new ApiClientError(failure);
    if (response.status === 401 && failure.kind === 'api' && authFailureHandler) {
      const context: AuthFailureContext = {
        method,
        path: path.split('?')[0] ?? path,
        ...(options.body === undefined ? {} : { body: options.body }),
        ...(idempotencyKey === null ? {} : { idempotencyKey }),
        ...(options.ifMatchVersion === undefined
          ? {}
          : { ifMatchVersion: String(options.ifMatchVersion) }),
        refusalId: requestId,
      };
      refusalIds.set(error, requestId);
      // While signing out, an unauthenticated answer is the point, not a
      // reason to sign in again.
      if (failure.code === 'UNAUTHENTICATED' && !signingOut) {
        authFailureHandler('unauthenticated', context);
      }
      if (failure.code === 'REAUTHENTICATION_REQUIRED') {
        authFailureHandler('reauthenticate', context);
      }
    }
    throw error;
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
